import { useAtom } from 'jotai';
import uniqBy from 'lodash/uniqBy';
import { useCallback } from 'react';

import { ToastProps } from 'common/components/cookbook/Toasts/Toast';
import { toastStateAtom } from 'common/state/toastState';
import { parseError } from 'common/utils/errorUtils';

import { useFormatMessage } from './useFormatMessage';
import { PartialBy } from '../types';

type AddToastOptions = PartialBy<ToastProps, 'id'>;

type AddSuccessToastOptions = Omit<AddToastOptions, 'variant'>;

export const useToast = () => {
  const [toasts, setToasts] = useAtom(toastStateAtom);
  const format = useFormatMessage();

  const addToast = useCallback(
    ({ id, ...options }: AddToastOptions) => {
      const newToast: ToastProps = {
        id: id ?? crypto.randomUUID(),
        ...options,
      };
      setToasts((prevToasts) => uniqBy([...prevToasts, newToast], (toast) => toast.id));
    },
    [setToasts],
  );

  const addErrorToast = useCallback(
    async (error: Error) => {
      const errorMessage = await parseError(error);
      addToast({
        variant: 'error',
        title: format('common.error'),
        description: errorMessage,
        autoClose: false,
      });
    },
    [addToast, format],
  );

  const addSuccessToast = useCallback(
    (options: AddSuccessToastOptions) =>
      addToast({
        variant: 'success',
        title: options.title ?? format('common.success'),
        ...options,
      }),
    [addToast, format],
  );

  const removeToast = useCallback(
    (id: string) => {
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    },
    [setToasts],
  );

  return {
    toasts,
    addToast,
    addErrorToast,
    addSuccessToast,
    removeToast,
  };
};
